/**
 * ProChallengeCard — owner dashboard card for the 4-Week Pro Challenge.
 * Renders nothing unless the owner has an enrollment. Data: GET /api/pro-challenge/mine.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Trophy, CheckCircle, XCircle, Circle, Clock, CalendarPlus } from "@phosphor-icons/react";

const WEEK_META = {
  completed: { icon: CheckCircle, cls: "text-green-600", label: "Completed" },
  current: { icon: Clock, cls: "text-amber-600", label: "In progress" },
  missed: { icon: XCircle, cls: "text-red-600", label: "Missed" },
  upcoming: { icon: Circle, cls: "text-muted-foreground/50", label: "Upcoming" },
};

const STATUS_CHIP = {
  active: ["In progress", "bg-amber-100 text-amber-800"],
  completed: ["Completed", "bg-green-100 text-green-800"],
  rewarded: ["Pro unlocked", "bg-green-100 text-green-800"],
  failed: ["Incomplete", "bg-red-100 text-red-700"],
  paused: ["Paused", "bg-slate-100 text-slate-700"],
  disqualified: ["Disqualified", "bg-red-100 text-red-700"],
  reward_expired: ["Reward ended", "bg-slate-100 text-slate-700"],
  converted_paid: ["Upgraded to Pro", "bg-green-100 text-green-800"],
};

const fmtDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

export const ProChallengeCard = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/pro-challenge/mine").then(({ data }) => setData(data)).catch(() => {});
  }, []);
  if (!data?.enrollment) return null;
  const enr = data.enrollment;
  const [chipLabel, chipCls] = STATUS_CHIP[enr.status] || [enr.status, "bg-slate-100 text-slate-700"];
  const done = data.completed_count || 0;
  const pct = Math.round((done / 4) * 100);
  const missedWeek = enr.weeks.find((w) => w.status === "missed");

  return (
    <div className="mt-4 rounded-2xl border bg-card p-5" data-testid="pro-challenge-card">
      <div className="flex flex-wrap items-center gap-2.5">
        <div className="h-10 w-10 rounded-xl bg-primary/10 grid place-items-center">
          <Trophy weight="duotone" className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-bold text-base tracking-tight">4-Week Pro Challenge</h3>
          <p className="text-xs text-muted-foreground">Post 1+ public stop each week · earn {data.reward_days} days of Pro free</p>
        </div>
        <span className={`text-[11px] font-bold rounded-full px-2.5 py-1 ${chipCls}`} data-testid="pro-challenge-status-chip">{chipLabel}</span>
      </div>

      {data.current_week_incomplete && enr.status === "active" && (
        <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2 text-sm font-medium" data-testid="pro-challenge-week-nudge">
          Week {data.current_week_index} isn't complete yet — post a stop before{" "}
          {data.next_deadline ? new Date(data.next_deadline).toLocaleDateString("en-US", { weekday: "long" }) : "the deadline"} to stay on track.
        </div>
      )}
      {enr.status === "failed" && missedWeek && (
        <div className="mt-3 rounded-lg bg-red-50 border border-red-200 text-red-800 px-3 py-2 text-sm font-medium" data-testid="pro-challenge-missed-banner">
          Week {missedWeek.index} ({fmtDate(missedWeek.start)}–{fmtDate(missedWeek.end)}) was missed. Reply to your challenge email if you'd like our team to review.
        </div>
      )}
      {enr.status === "rewarded" && enr.reward?.expires_at && (
        <div className="mt-3 rounded-lg bg-green-50 border border-green-200 text-green-800 px-3 py-2 text-sm font-medium" data-testid="pro-challenge-reward-banner">
          Pro is active — free until {new Date(enr.reward.expires_at).toLocaleDateString()}. No card, no auto-billing.
        </div>
      )}

      <div className="mt-4 h-2 rounded-full bg-muted overflow-hidden" data-testid="pro-challenge-progress-bar">
        <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1 text-xs text-muted-foreground font-medium">{done} of 4 weeks complete</div>

      <div className="mt-3 grid gap-1.5">
        {enr.weeks.map((w) => {
          const m = WEEK_META[w.status] || WEEK_META.upcoming;
          const Icon = m.icon;
          return (
            <div key={w.index} className="flex items-center gap-2.5 text-sm" data-testid={`pro-challenge-week-${w.index}`}>
              <Icon weight={w.status === "completed" ? "fill" : "regular"} className={`h-4.5 w-4.5 h-5 w-5 flex-none ${m.cls}`} />
              <span className="font-semibold w-16">Week {w.index}</span>
              <span className="text-muted-foreground">{fmtDate(w.start)} – {fmtDate(w.end)}</span>
              <span className={`ml-auto text-xs font-medium ${m.cls}`}>{m.label}</span>
            </div>
          );
        })}
      </div>
      {enr.weeks.some((w) => w.status === "completed" && w.qualifying_event?.date) && (
        <div className="mt-2 text-xs text-muted-foreground">
          {enr.weeks.filter((w) => w.status === "completed" && w.qualifying_event?.date).map((w) => (
            <div key={w.index} data-testid={`pro-challenge-week-${w.index}-event`}>
              Week {w.index}: {w.qualifying_event.event_name || w.qualifying_event.address} · {fmtDate(w.qualifying_event.date)}
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        {enr.status === "active" && (
          <Button asChild size="sm" className="rounded-full gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="pro-challenge-post-btn">
            <Link to="/dashboard/schedule-post"><CalendarPlus className="h-4 w-4" /> Post my schedule</Link>
          </Button>
        )}
        <Link to="/pricing" className="text-sm font-medium text-primary hover:underline" data-testid="pro-challenge-pro-features-link">
          See what's included in Pro →
        </Link>
      </div>
      <p className="mt-2.5 text-[11px] text-muted-foreground leading-relaxed">
        Complete all four weeks and TruckTrackr Pro unlocks automatically for {data.reward_days} days — no credit card, no auto-renewal, no charge at the end.
      </p>
    </div>
  );
};
